import { useToast } from '@airship-platform/airship-patterns';
import { DiscountAndFee } from '@airship-platform/airship-proposal';
import { IconButtonProps } from '@mui/material/IconButton';
import { useMemo } from 'react';
import { useAppointmentProposalOptionModifiers } from '../../data-access/appointmentProposalOptionModifiers';
import { AppointmentProposalOption } from '../../data-access/appointmentProposalOptions';
import { QuickAddButton } from '../../patterns';

interface QuickAddModifierButtonProps extends IconButtonProps {
  modifier: DiscountAndFee;
  option?: AppointmentProposalOption;
}

const QuickAddModifierButton: React.FC<QuickAddModifierButtonProps> = ({
  modifier,
  option,
  ...props
}) => {
  const {
    createAppointmentProposalOptionModifier,
    getNextSortOrder,
    proposalModifiers,
    isLoadingModifierIds,
  } = useAppointmentProposalOptionModifiers();

  const { showToast } = useToast();

  const isAlreadyAdded = useMemo(
    () =>
      option
        ? proposalModifiers.some(
            (pm) =>
              pm.appointmentProposalOptionId ===
                option.appointmentProposalOptionId &&
              pm.discountsAndFeesId === modifier.discountsAndFeesId
          )
        : false,
    [proposalModifiers, option, modifier.discountsAndFeesId]
  );

  if (!option) {
    return null;
  }

  const onClick = () =>
    createAppointmentProposalOptionModifier(
      option.appointmentId,
      option.appointmentProposalId,
      option.appointmentProposalOptionId,
      {
        ...modifier,
        jobId: option.jobId,
        sortOrder: getNextSortOrder(),
      }
    ).then(() => {
      showToast(`Added ${modifier.displayName} to ${option.name}`, 'success');
    });

  return (
    <QuickAddButton
      isLoading={isLoadingModifierIds.includes(modifier.discountsAndFeesId)}
      isAlreadyAdded={isAlreadyAdded}
      disabled={!option || isAlreadyAdded || option.isReadOnly}
      onClick={onClick}
    />
  );
};

export default QuickAddModifierButton;
